import { useEffect, useRef, useState } from "react";
import { jalaaliMonthLength, toGregorian, toJalaali } from "jalaali-js";
import SVG from "../../../Components/SVG";
import Custom from "../../../Components/Buttons/Custom";

const ITEM_HEIGHT = 44;
const WHEEL_HEIGHT = 220;
const EDGE_SPACER_HEIGHT = (WHEEL_HEIGHT - ITEM_HEIGHT) / 2;
const MONTH_NAMES = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];
const PERSIAN_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];

function toPersianDigits(value) {
  return String(value).replace(/\d/g, (digit) => PERSIAN_DIGITS[Number(digit)]);
}

function formatGregorianDate({ gy, gm, gd }) {
  return `${gy}-${String(gm).padStart(2, "0")}-${String(gd).padStart(2, "0")}`;
}

function parseGregorianDate(value) {
  if (!value) return null;

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (!match) return null;

  return {
    gy: Number(match[1]),
    gm: Number(match[2]),
    gd: Number(match[3]),
  };
}

function isValidGregorianDate({ gy, gm, gd }) {
  if (gm < 1 || gm > 12 || gd < 1 || gd > 31) {
    return false;
  }

  const date = new Date(Date.UTC(gy, gm - 1, gd));

  return (
    date.getUTCFullYear() === gy &&
    date.getUTCMonth() === gm - 1 &&
    date.getUTCDate() === gd
  );
}

function convertGregorianToJalali(value) {
  const parsedDate = parseGregorianDate(value);

  if (!parsedDate || !isValidGregorianDate(parsedDate)) {
    return null;
  }

  try {
    return toJalaali(parsedDate.gy, parsedDate.gm, parsedDate.gd);
  } catch {
    return null;
  }
}

function compareJalaliDates(a, b) {
  if (a.jy !== b.jy) return a.jy - b.jy;
  if (a.jm !== b.jm) return a.jm - b.jm;
  return a.jd - b.jd;
}

function clampJalaliDate(date, minDate, maxDate) {
  const safeDay = Math.min(date.jd, jalaaliMonthLength(date.jy, date.jm));
  const safeDate = { ...date, jd: safeDay };

  if (compareJalaliDates(safeDate, minDate) < 0) {
    return { ...minDate };
  }

  if (compareJalaliDates(safeDate, maxDate) > 0) {
    return { ...maxDate };
  }

  return safeDate;
}

function getGregorianRange() {
  const today = new Date();
  const maxDate = {
    gy: today.getFullYear(),
    gm: today.getMonth() + 1,
    gd: today.getDate(),
  };
  const minDateValue = new Date(
    today.getFullYear() - 120,
    today.getMonth(),
    today.getDate(),
  );

  return {
    maxDate,
    minDate: {
      gy: minDateValue.getFullYear(),
      gm: minDateValue.getMonth() + 1,
      gd: minDateValue.getDate(),
    },
  };
}

function getInitialJalaliDate(value, fallbackDate, minDate, maxDate) {
  const jalaliDate = convertGregorianToJalali(value);

  if (!jalaliDate) {
    return { ...fallbackDate };
  }

  return clampJalaliDate(jalaliDate, minDate, maxDate);
}

function formatJalaliLabel(value) {
  const jalaliDate = convertGregorianToJalali(value);

  if (!jalaliDate) return "انتخاب تاریخ تولد";

  const { jy, jm, jd } = jalaliDate;

  return `${toPersianDigits(jd)} ${MONTH_NAMES[jm - 1]} ${toPersianDigits(jy)}`;
}

function WheelPickerColumn({ options, selectedValue, onChange }) {
  const scrollRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const selectedIndex = Math.max(
    options.findIndex((option) => option.value === selectedValue),
    0,
  );

  useEffect(() => {
    const scrollNode = scrollRef.current;

    if (!scrollNode) return;

    scrollNode.scrollTo({
      top: selectedIndex * ITEM_HEIGHT,
    });
  }, [selectedIndex, options.length]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const syncValueWithScroll = () => {
    const scrollNode = scrollRef.current;

    if (!scrollNode || options.length === 0) return;

    const nextIndex = Math.min(
      Math.max(Math.round(scrollNode.scrollTop / ITEM_HEIGHT), 0),
      options.length - 1,
    );
    const nextValue = options[nextIndex]?.value;

    if (nextValue !== undefined && nextValue !== selectedValue) {
      onChange(nextValue);
    }
  };

  return (
    <div className="relative flex-1">
      <div className="pointer-events-none absolute inset-x-0 top-1/2 h-11 -translate-y-1/2 rounded-lg border-none border-zi-border-light bg-zi-bg1-light/50 dark:border-none dark:bg-zi-inputbg-dark/50" />
      <div
        ref={scrollRef}
        onScroll={() => {
          if (scrollTimeoutRef.current) {
            window.clearTimeout(scrollTimeoutRef.current);
          }

          scrollTimeoutRef.current = window.setTimeout(syncValueWithScroll, 80);
        }}
        className="h-[220px] overflow-y-auto snap-y snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        <div style={{ height: `${EDGE_SPACER_HEIGHT}px` }} />
        {options.map((option) => {
          const isSelected = option.value === selectedValue;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={`flex h-11 w-full snap-center items-center justify-center px-3 text-sm transition ${
                isSelected
                  ? "font-medium text-zi-text-light dark:text-zi-text-dark relative"
                  : "text-zi-text-light/50 dark:text-zi-text-dark/50"
              }`}
            >
              {option.label}
            </button>
          );
        })}
        <div style={{ height: `${EDGE_SPACER_HEIGHT}px` }} />
      </div>
    </div>
  );
}

export default function BirthDatePicker({
  label = "",
  value,
  onChange,
  isReadOnly = true,
}) {
  const { minDate: minGregorianDate, maxDate: maxGregorianDate } =
    getGregorianRange();
  const minJalaliDate = toJalaali(
    minGregorianDate.gy,
    minGregorianDate.gm,
    minGregorianDate.gd,
  );
  const minJalaliYear = minJalaliDate.jy;
  const minJalaliMonth = minJalaliDate.jm;
  const minJalaliDay = minJalaliDate.jd;
  const maxJalaliDate = toJalaali(
    maxGregorianDate.gy,
    maxGregorianDate.gm,
    maxGregorianDate.gd,
  );
  const maxJalaliYear = maxJalaliDate.jy;
  const maxJalaliMonth = maxJalaliDate.jm;
  const maxJalaliDay = maxJalaliDate.jd;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draftDate, setDraftDate] = useState(() =>
    getInitialJalaliDate(value, maxJalaliDate, minJalaliDate, maxJalaliDate),
  );

  useEffect(() => {
    if (!isModalOpen) {
      const currentMinJalaliDate = {
        jy: minJalaliYear,
        jm: minJalaliMonth,
        jd: minJalaliDay,
      };
      const currentMaxJalaliDate = {
        jy: maxJalaliYear,
        jm: maxJalaliMonth,
        jd: maxJalaliDay,
      };

      setDraftDate(
        getInitialJalaliDate(
          value,
          currentMaxJalaliDate,
          currentMinJalaliDate,
          currentMaxJalaliDate,
        ),
      );
    }
  }, [
    value,
    isModalOpen,
    minJalaliDay,
    minJalaliMonth,
    minJalaliYear,
    maxJalaliDay,
    maxJalaliMonth,
    maxJalaliYear,
  ]);

  useEffect(() => {
    if (!isReadOnly || !isModalOpen) return;

    setIsModalOpen(false);
  }, [isReadOnly, isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) return;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsModalOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpen]);

  const yearOptions = [];

  for (let year = maxJalaliDate.jy; year >= minJalaliDate.jy; year -= 1) {
    yearOptions.push({
      value: year,
      label: toPersianDigits(year),
    });
  }

  const minMonth = draftDate.jy === minJalaliDate.jy ? minJalaliDate.jm : 1;
  const maxMonth = draftDate.jy === maxJalaliDate.jy ? maxJalaliDate.jm : 12;
  const monthOptions = [];

  for (let month = minMonth; month <= maxMonth; month += 1) {
    monthOptions.push({
      value: month,
      label: MONTH_NAMES[month - 1],
    });
  }

  const minDay =
    draftDate.jy === minJalaliDate.jy && draftDate.jm === minJalaliDate.jm
      ? minJalaliDate.jd
      : 1;
  const maxDay =
    draftDate.jy === maxJalaliDate.jy && draftDate.jm === maxJalaliDate.jm
      ? Math.min(
          maxJalaliDate.jd,
          jalaaliMonthLength(draftDate.jy, draftDate.jm),
        )
      : jalaaliMonthLength(draftDate.jy, draftDate.jm);
  const dayOptions = [];

  for (let day = minDay; day <= maxDay; day += 1) {
    dayOptions.push({
      value: day,
      label: toPersianDigits(String(day).padStart(2, "0")),
    });
  }

  const openPicker = () => {
    if (isReadOnly) return;

    setDraftDate(
      getInitialJalaliDate(value, maxJalaliDate, minJalaliDate, maxJalaliDate),
    );
    setIsModalOpen(true);
  };

  const updateDraftDate = (nextDate) => {
    setDraftDate((previousDate) =>
      clampJalaliDate(
        { ...previousDate, ...nextDate },
        minJalaliDate,
        maxJalaliDate,
      ),
    );
  };

  const handleSubmit = () => {
    const gregorianDate = toGregorian(draftDate.jy, draftDate.jm, draftDate.jd);

    onChange(formatGregorianDate(gregorianDate));
    setIsModalOpen(false);
  };
  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      setIsModalOpen(false);
    }
  };

  return (
    <>
      <div
        className={`relative flex h-[42px] w-full items-center overflow-visible rounded-lg border-none transition-colors ${
          isReadOnly
            ? "border-transparent bg-transparent"
            : "border-zi-border-light bg-transparent dark:border-none dark:bg-zi-inputbg-dark hocus-within:border-zi-border-dark hocus-within:dark:border-zi-border-dark/50"
        }`}
      >
        <button
          type="button"
          onClick={openPicker}
          className={`relative h-full w-full bg-transparent px-3 text-start text-sm text-zi-text-light outline-none dark:text-zi-text-dark  ${
            isReadOnly ? "cursor-default" : ""
          }`}
        >
          {value ? formatJalaliLabel(value) : <span className="opacity-0">انتخاب تاریخ تولد</span>}
          <span className={`absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none ${isReadOnly ? "opacity-0" : "opacity-100"}`}>
          <SVG name="calendar" classname="stroke-zi-text-light dark:stroke-zi-text-dark fill-zi-text-light dark:fill-zi-text-dark" />
          </span>
        </button>
        {label && (
          <span
            className={`pointer-events-none absolute right-0 top-0 z-10 origin-right -translate-y-1/2 rounded-lg rounded-b-none px-2.5 py-1 text-xs leading-none text-zi-blue-light/50 transition-colors duration-200 ease-in-out dark:text-zi-text-dark/50 ${
              !isReadOnly ? "dark:bg-zi-inputbg-dark" : "bg-transparent"
            }`}
          >
            {label}
          </span>
        )}
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur"
          onClick={handleOverlayClick}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-sm rounded-2xl bg-zi-bg2-light p-5 shadow-md dark:bg-zi-bg2-dark"
          >
            <div className="mb-5 text-right">
              <h3 className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
                انتخاب تاریخ تولد
              </h3>
              <p className="mt-1 text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
                تاریخ را به صورت هجری شمسی انتخاب کنید.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <WheelPickerColumn
                options={dayOptions}
                selectedValue={draftDate.jd}
                onChange={(day) => updateDraftDate({ jd: day })}
              />
              <WheelPickerColumn
                options={monthOptions}
                selectedValue={draftDate.jm}
                onChange={(month) => updateDraftDate({ jm: month })}
              />
              <WheelPickerColumn
                options={yearOptions}
                selectedValue={draftDate.jy}
                onChange={(year) => updateDraftDate({ jy: year })}
              />
            </div>

            <div className="mt-5 rounded-lg bg-zi-bg1-light px-4 py-2 text-center text-sm text-zi-text-light dark:bg-zi-inputbg-dark dark:text-zi-text-dark">
              {toPersianDigits(draftDate.jd)} {MONTH_NAMES[draftDate.jm - 1]}{" "}
              {toPersianDigits(draftDate.jy)}
            </div>

            <div className="mt-5 flex items-center justify-end gap-3">
              <Custom text="بستن" onClick={() => setIsModalOpen(false)} className="w-full border border-zi-border-dark !text-sm !rounded-lg !py-1.5 !px-3 bg-transparent text-zi-text-light dark:text-zi-text-dark hocus:ring-1 hocus:ring-inset hocus:ring-zi-border-dark"/>
              <Custom text="ثبت" onClick={handleSubmit} className="w-full !text-sm !rounded-lg !py-1.5 !px-3 bg-zi-buttonbg-primary text-zi-text-dark hocus:opacity-85"/>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
